<div class="container-fluid">
  <div class="row">
    <div class="col-12">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h3>SIM Details</h3>
        <button class="btn btn-secondary" (click)="goBack()">Back to SIMs</button>
      </div>

      <!-- Loading indicator -->
      @if (loading) {
        <div class="d-flex justify-content-center mt-5">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      }

      <!-- Error message -->
      @if (error && !loading) {
        <div class="alert alert-danger mt-3">
          {{ error }}
        </div>
      }

      <!-- SIM details -->
      @if (!loading && !error && sim) {
        <div class="mt-4">
          <!-- Actions Card -->
          <div class="card mb-4">
            <div class="card-header bg-dark text-white">
              <h4 class="mb-0">Actions</h4>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-2 col-6 mb-2">
                  <button class="btn btn-primary w-100" (click)="viewUsage()">Usage</button>
                </div>
                <div class="col-md-2 col-6 mb-2">
                  <button class="btn btn-outline-primary w-100" (click)="viewPackageHistory()">Packages</button>
                </div>
                <div class="col-md-2 col-6 mb-2">
                  <button class="btn btn-outline-info w-100" (click)="viewInstructions()">Instructions</button>
                </div>
                <div class="col-md-2 col-6 mb-2">
                  <button class="btn btn-light w-100" (click)="viewCompatibleDevices()">Compatible Devices</button>
                </div>
                <div class="col-md-2 col-6 mb-2">
                  @if (sim.qrcode_url) {
                    <a [href]="sim.qrcode_url" target="_blank" class="btn btn-outline-success w-100">QR Code</a>
                  }
                </div>
                <div class="col-md-2 col-6 mb-2">
                  @if (sim.direct_apple_installation_url) {
                    <a [href]="sim.direct_apple_installation_url" target="_blank" class="btn btn-outline-primary w-100">Apple Install</a>
                  }
                </div>
              </div>
            </div>
          </div>
          <!-- SIM Information Card -->
          <div class="card mb-4">
            <div class="card-header bg-primary text-white">
              <h4 class="mb-0">SIM Information</h4>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label fw-bold">SIM ID</label>
                    <p class="mb-0">{{ sim.id }}</p>
                  </div>
                  <div class="mb-3">
                    <label class="form-label fw-bold">ICCID</label>
                    <p class="mb-0">{{ sim.iccid }}</p>
                  </div>
                  <div class="mb-3">
                    <label class="form-label fw-bold">LPA</label>
                    <p class="mb-0">{{ sim.lpa }}</p>
                  </div>
                  <div class="mb-3">
                    <label class="form-label fw-bold">Matching ID</label>
                    <p class="mb-0">{{ sim.matching_id }}</p>
                  </div>
                  <div class="mb-3">
                    <label class="form-label fw-bold">APN Type</label>
                    <p class="mb-0">{{ sim.apn_type }}</p>
                  </div>
                  @if (sim.apn_value) {
                    <div class="mb-3">
                      <label class="form-label fw-bold">APN Value</label>
                      <p class="mb-0">{{ sim.apn_value }}</p>
                    </div>
                  }
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label fw-bold">Roaming</label>
                    <p class="mb-0">
                      <span class="badge" [class]="'badge-' + (sim.is_roaming ? 'success' : 'secondary')">
                        {{ sim.is_roaming ? 'Enabled' : 'Disabled' }}
                      </span>
                    </p>
                  </div>
                  <div class="mb-3">
                    <label class="form-label fw-bold">Created At</label>
                    <p class="mb-0">{{ formatDate(sim.created_at) }}</p>
                  </div>
                  @if (sim.msisdn) {
                    <div class="mb-3">
                      <label class="form-label fw-bold">MSISDN</label>
                      <p class="mb-0">{{ sim.msisdn }}</p>
                    </div>
                  }
                  @if (sim.airalo_code) {
                    <div class="mb-3">
                      <label class="form-label fw-bold">Airalo Code</label>
                      <p class="mb-0">{{ sim.airalo_code }}</p>
                    </div>
                  }
                  @if (sim.voucher_code) {
                    <div class="mb-3">
                      <label class="form-label fw-bold">Voucher Code</label>
                      <p class="mb-0">{{ sim.voucher_code }}</p>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
          <!-- APN Settings Card -->
          <div class="card mb-4">
            <div class="card-header bg-info text-white">
              <h4 class="mb-0">APN Settings</h4>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <h5>iOS Settings</h5>
                  <div class="mb-3">
                    <label class="form-label fw-bold">APN Type</label>
                    <p class="mb-0">{{ sim.apn.ios.apn_type }}</p>
                  </div>
                  @if (sim.apn.ios.apn_value) {
                    <div class="mb-3">
                      <label class="form-label fw-bold">APN Value</label>
                      <p class="mb-0">{{ sim.apn.ios.apn_value }}</p>
                    </div>
                  }
                </div>
                <div class="col-md-6">
                  <h5>Android Settings</h5>
                  <div class="mb-3">
                    <label class="form-label fw-bold">APN Type</label>
                    <p class="mb-0">{{ sim.apn.android.apn_type }}</p>
                  </div>
                  @if (sim.apn.android.apn_value) {
                    <div class="mb-3">
                      <label class="form-label fw-bold">APN Value</label>
                      <p class="mb-0">{{ sim.apn.android.apn_value }}</p>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
          <!-- Sharing Information Card -->
          @if (sim.sharing) {
            <div class="card mb-4">
              <div class="card-header bg-success text-white">
                <h4 class="mb-0">Sharing Information</h4>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label fw-bold">Sharing Link</label>
                      <div class="input-group">
                        <input type="text" class="form-control" [value]="sim.sharing.link" readonly>
                        <button class="btn btn-light" type="button" (click)="copyToClipboard(sim.sharing.link)">
                          <i class="fas fa-copy"></i> Copy
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label fw-bold">Access Code</label>
                      <p class="mb-0">{{ sim.sharing.access_code }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
          <!-- Additional Information Card -->
          <div class="card">
            <div class="card-header bg-secondary text-white">
              <h4 class="mb-0">Additional Information</h4>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  @if (sim.imsis) {
                    <div class="mb-3">
                      <label class="form-label fw-bold">IMSI</label>
                      <p class="mb-0">{{ sim.imsis }}</p>
                    </div>
                  }
                  @if (sim.confirmation_code) {
                    <div class="mb-3">
                      <label class="form-label fw-bold">Confirmation Code</label>
                      <p class="mb-0">{{ sim.confirmation_code }}</p>
                    </div>
                  }
                  @if (sim.brand_settings_name) {
                    <div class="mb-3">
                      <label class="form-label fw-bold">Brand Settings Name</label>
                      <p class="mb-0">{{ sim.brand_settings_name }}</p>
                    </div>
                  }
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label fw-bold">Recycled</label>
                    <p class="mb-0">
                      <span class="badge" [class]="'badge-' + (sim.recycled ? 'danger' : 'success')">
                        {{ sim.recycled ? 'Yes' : 'No' }}
                      </span>
                    </p>
                  </div>
                  @if (sim.recycled_at) {
                    <div class="mb-3">
                      <label class="form-label fw-bold">Recycled At</label>
                      <p class="mb-0">{{ sim.recycled_at }}</p>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      }

      <!-- No SIM message -->
      @if (!loading && !error && !sim) {
        <div class="alert alert-info mt-3">
          SIM not found.
        </div>
      }
    </div>
  </div>
</div>